import { useCallback, RefObject } from "react";
import { AgGridReact } from "ag-grid-react";
import { CellValueChangedEvent } from "ag-grid-community";
import { PRD2010_R01Dto } from "../Dto_PRD2010_R01";
import { EmployeeDto } from "src/dtos/employee/employeeDto";
import { WorkPlaceDto } from "src/dtos/workplace/workPlaceDto";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { createSearchFunctionFromCategory } from "src/utils/categoryUtils";
import { focusToCell } from "src/utils/grid/GridHandlerContent";

/** 작업공정 드롭다운 변경 시 연동 필드 (GridDropDown onChange 경로용) */
export function getWorkplaceChangePatch(
	workPlaceList: WorkPlaceDto[],
	employeeCdList: EmployeeDto[],
	newValue: string,
): Pick<PRD2010_R01Dto, "workProcess" | "orderEmp" | "orderEmpNm"> {
	const usableList = workPlaceList.filter((opt) => opt.useYn === "Y" && opt.orderYn === "Y");
	const found = usableList.find((opt) => opt.workplaceCd === newValue);
	if (found) {
		return {
			workProcess: found.workProcess,
			orderEmp: found.workEmpNo ?? "",
			orderEmpNm: employeeCdList.find((item) => item.empNo === found.workEmpNo)?.empNm ?? "",
		};
	}
	return {
		workProcess: "",
		orderEmp: "",
		orderEmpNm: "",
	};
}

interface UsePRD2010_R01CellChangeProps {
	updatePRD2010_R01List: (index: number, data: PRD2010_R01Dto) => void;
	originalPRD2010_R01List: PRD2010_R01Dto[];
	gridRef: RefObject<AgGridReact<PRD2010_R01Dto> | null>;
	settingCodeSearchModalProps: (
		category: string,
		search: string,
		isInfo: boolean,
		handleFocusId: string,
		originalData: PRD2010_R01Dto,
		valueIndex: number,
	) => void;
}

export function usePRD2010_R01CellChange({
	updatePRD2010_R01List,
	originalPRD2010_R01List,
	gridRef,
	settingCodeSearchModalProps,
}: UsePRD2010_R01CellChangeProps) {
	const { workPlaceList, employeeCdList } = useCommonCodeStore();

	const handleItemCdSearch = useCallback(
		async (newValue: string, rowData: PRD2010_R01Dto) => {
			if (!newValue || !newValue.trim()) {
				updatePRD2010_R01List(rowData.valueIndex!, {
					...rowData,
					itemCd: "",
					itemNm: "",
					itemSpec: "",
					itemUnit: "",
					isModified: rowData.inserted ? false : true,
				});
				return;
			}

			const searchFn = createSearchFunctionFromCategory("item");
			if (!searchFn) {
				settingCodeSearchModalProps("품목", newValue, false, "itemCd", rowData, rowData.valueIndex!);
				return;
			}

			try {
				const found = await searchFn(newValue.trim().toUpperCase(), (item: any) => {
					updatePRD2010_R01List(rowData.valueIndex!, {
						...rowData,
						itemCd: item.itemCd ?? "",
						itemNm: item.itemNm ?? "",
						itemSpec: item.itemSpec ?? "",
						itemUnit: item.itemUnit ?? "",
						isModified: rowData.inserted ? false : true,
					});
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							focusToCell(gridRef, rowData.valueIndex!, "orderQty");
						});
					});
				});

				if (!found) {
					settingCodeSearchModalProps("품목", newValue, false, "itemCd", rowData, rowData.valueIndex!);
				}
			} catch {
				settingCodeSearchModalProps("품목", newValue, false, "itemCd", rowData, rowData.valueIndex!);
			}
		},
		[updatePRD2010_R01List, settingCodeSearchModalProps, gridRef],
	);

	const handleOrderEmpSearch = useCallback(
		async (newValue: string, rowData: PRD2010_R01Dto) => {
			if (!newValue || !newValue.trim()) {
				updatePRD2010_R01List(rowData.valueIndex!, {
					...rowData,
					orderEmp: "",
					orderEmpNm: "",
					isModified: rowData.inserted ? false : true,
				});
				return;
			}

			const searchFn = createSearchFunctionFromCategory("employee");
			if (!searchFn) {
				settingCodeSearchModalProps("작업자", newValue, false, "orderEmp", rowData, rowData.valueIndex!);
				return;
			}

			try {
				const found = await searchFn(newValue.trim().toUpperCase(), (item: any) => {
					updatePRD2010_R01List(rowData.valueIndex!, {
						...rowData,
						orderEmp: item.empNo ?? "",
						orderEmpNm: item.empNm ?? "",
						isModified: rowData.inserted ? false : true,
					});
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							focusToCell(gridRef, rowData.valueIndex!, "itemCd");
						});
					});
				});

				if (!found) {
					settingCodeSearchModalProps("작업자", newValue, false, "orderEmp", rowData, rowData.valueIndex!);
				}
			} catch {
				settingCodeSearchModalProps("작업자", newValue, false, "orderEmp", rowData, rowData.valueIndex!);
			}
		},
		[updatePRD2010_R01List, settingCodeSearchModalProps, gridRef],
	);

	const handleBasOrderSearch = useCallback(
		async (newValue: string, rowData: PRD2010_R01Dto) => {
			if (!newValue || !newValue.trim()) {
				updatePRD2010_R01List(rowData.valueIndex!, {
					...rowData,
					basOrderNo: "",
					basOrderItemNo: "",
					customNm: "",
					itemCd: "",
					itemNm: "",
					itemSpec: "",
					itemUnit: "",
					orderQty: 0,
					isModified: rowData.inserted ? false : true,
				});
				return;
			}

			const searchFn = createSearchFunctionFromCategory("salesOrder");
			if (!searchFn) {
				settingCodeSearchModalProps("수주번호", newValue, false, "basOrderNo", rowData, rowData.valueIndex!);
				return;
			}

			try {
				const found = await searchFn(
					newValue.trim().toUpperCase(), 
					(item: any) => {
						updatePRD2010_R01List(rowData.valueIndex!, {
							...rowData,
							basOrderNo: item.basOrderNo ?? item.salesOrderNo ?? "",
							basOrderItemNo: item.basOrderItemNo ?? item.itemNo ?? "",
							customNm: item.customNm ?? "",
							itemCd: item.itemCd ?? "",
							itemNm: item.itemNm ?? "",
							itemSpec: item.itemSpec ?? "",
							itemUnit: item.orderUnit ?? item.itemUnit ?? "",
							orderQty: Number(item.orderQty) || 0,
							isModified: rowData.inserted ? false : true,
						});
						requestAnimationFrame(() => {
							requestAnimationFrame(() => {
								focusToCell(gridRef, rowData.valueIndex!, "orderQty");
							});
						});
					},
				);

				if (!found) {
					settingCodeSearchModalProps("수주번호", newValue, false, "basOrderNo", rowData, rowData.valueIndex!);
				}
			} catch {
				settingCodeSearchModalProps("수주번호", newValue, false, "basOrderNo", rowData, rowData.valueIndex!);
			}
		},
		[updatePRD2010_R01List, settingCodeSearchModalProps, gridRef],
	);

	const handleCellChange = useCallback(
		async (event: CellValueChangedEvent<PRD2010_R01Dto>) => {
			const { data, colDef, newValue, oldValue } = event;
			if (!data || !colDef.field) return;
			const field = colDef.field as keyof PRD2010_R01Dto;

			if (
				(field === "itemCd" || field === "orderEmp" || field === "basOrderNo" || field === "basOrderItemNo")
				&& newValue !== oldValue
			) {
				updatePRD2010_R01List(data.valueIndex!, {
					...data,
					[field]: newValue,
					isModified: data.inserted ? false : true,
				});

				if (field === "itemCd") {
					await handleItemCdSearch(newValue, data);
				} else if (field === "orderEmp") {
					await handleOrderEmpSearch(newValue, data);
				} else {
					await handleBasOrderSearch(newValue, data);
				}
				return;
			}

			let newData = { ...data };

			if (field === "compCd") {
				newData.workplaceCd = "";
			}

			if (field === "workplaceCd") {
				Object.assign(newData, getWorkplaceChangePatch(workPlaceList, employeeCdList, newValue));
			}

			let isModified = newData.inserted ? false : true;
			if (field === "orderDt") {
				const originRow = originalPRD2010_R01List.find((item) => item.valueIndex === newData.valueIndex);
				if (originRow && originRow.orderDt === newValue) {
					isModified = false;
				}
			}

			updatePRD2010_R01List(newData.valueIndex!, {
				...newData,
				[field]: newValue,
				isModified,
			});
		},
		[
			handleItemCdSearch,
			handleOrderEmpSearch,
			handleBasOrderSearch,
			updatePRD2010_R01List,
			workPlaceList,
			employeeCdList,
			originalPRD2010_R01List,
		],
	);

	return { handleCellChange };
}
